import { useApiCampaignMember } from '@hooks-api/fortune/useApiCampaignMember'
import { Box } from '@pets-ui/Box/Box'
import { HStack } from '@pets-ui/HStack/HStack'
import { IconButton } from '@pets-ui/IconButton/IconButton'
import { Menu, UserOutlined } from '@pets-ui/icons'
import { Image } from '@pets-ui/Image/Image'
import { Link } from '@pets-ui/Link/Link'
import { Text } from '@pets-ui/Text/Text'
import { colors } from '@pets-ui/tokens/colors.stylex'
import { useResponsive } from '@pets-ui/useResponsive/useResponsive'
import * as stylex from '@stylexjs/stylex'
import { type ReactNode, useCallback, useMemo, useState } from 'react'
import { useHistory, useRouteMatch } from 'react-router-dom-v5'
import { AppBarCart } from '../../../../components/AppBarCart/AppBarCart'
import stmLogo from '../../assets/shepherdtech-logo.png'
import { type BrandCategory, type BrandInfo, NAV_CATEGORIES, NAV_LINKS, STM_COUNTRY_CONFIG } from '../../constants'
import { useActiveBrands, useCountryCode, useStmAllBrandsCartQuantity, useStmBrandStore } from '../../hooks'
import { AppBar } from './AppBar'
import { BrandNavDrawer } from './BrandNavDrawer'
import { BrandNavDropdown } from './BrandNavDropdown'

const styles = stylex.create({
  hoverUnderline: {
    textDecorationColor: {
      'default': 'transparent',
      ':hover': colors.grey900,
    },
    textDecorationLine: 'underline',
    textDecorationThickness: '1px',
    textUnderlineOffset: '4px',
  },
  navItemActive: {
    textDecorationColor: colors.grey900,
  },
})

interface HeaderProps {
  onLogout?: () => void
  isAuth?: boolean
}

interface NavItemProps {
  active?: boolean
  path?: string
  onClick: () => void
  children: ReactNode
}

const NavItem = (props: NavItemProps) => {
  const { active, path, onClick, children } = props
  const routeMatch = useRouteMatch({ path: path ?? '' })
  const isActive = active ?? (path ? routeMatch !== null : false)

  return (
    <Box
      p={3}
      cursor="pointer"
      rootStyle={[styles.hoverUnderline, isActive && styles.navItemActive]}
      onClick={onClick}
    >
      <Text bold={isActive} color={colors.grey800}>{children}</Text>
    </Box>
  )
}

const LogoHeader = (props: { basePath: string }) => {
  const { basePath } = props
  const countryCode = useCountryCode()
  const history = useHistory()

  return (
    <Link onClick={() => history.push(basePath)}>
      <HStack gap={2} alignItems="center">
        <Image src={stmLogo} alt="牧羊人經銷平台" width={80} height={32} objectFit="contain" />
        <Text variant="subtitle" color={colors.grey900}>
          牧羊人經銷平台｜
          {STM_COUNTRY_CONFIG[countryCode]?.label}
        </Text>
      </HStack>
    </Link>
  )
}

const DesktopLeftHeader = (props: {
  activeCategory: BrandCategory | null
  onToggleCategory: (category: BrandCategory) => void
  onClearCategory: () => void
  basePath: string
}) => {
  const { activeCategory, onToggleCategory, onClearCategory, basePath } = props
  const { brands } = useStmBrandStore()
  const history = useHistory()

  /** 確保沒有該分類的品牌時，不顯示該分類 */
  const visibleCategories = useMemo(() =>
    NAV_CATEGORIES.filter(item => brands.some(brand => brand.category === item.category)),
  [brands])

  return (
    <HStack gap={3} alignItems="center">
      <LogoHeader basePath={basePath} />
      <HStack alignItems="center">
        {NAV_LINKS.map(item => (
          <NavItem
            key={item.to}
            path={`${basePath}${item.to}`}
            onClick={() => {
              onClearCategory()
              history.push(`${basePath}${item.to}`)
            }}
          >
            {item.label}
          </NavItem>
        ))}
        {visibleCategories.map(item => (
          <NavItem
            key={item.category}
            active={activeCategory === item.category}
            onClick={() => onToggleCategory(item.category)}
          >
            {item.label}
          </NavItem>
        ))}
      </HStack>
    </HStack>
  )
}

const DesktopRightHeader = (props: { onLogout?: () => void, basePath: string, userName: string, cartItemQuantity: number }) => {
  const { onLogout, basePath, userName, cartItemQuantity } = props
  const history = useHistory()

  return (
    <HStack gap={2} alignItems="center">
      <Link onClick={onLogout}>
        <Text variant="caption" color={colors.blue600}>登出</Text>
      </Link>
      <Text variant="caption" color={colors.grey600}>{userName}</Text>
      <HStack>
        <IconButton onClick={() => history.push(`${basePath}/my-account/order`)} width="32px" height="32px">
          <UserOutlined color={colors.grey900} fontSize={20} />
        </IconButton>
        <AppBarCart
          onClick={() => history.push(`${basePath}/cart`)}
          cartItemQuantity={cartItemQuantity}
        />
      </HStack>
    </HStack>
  )
}

const MobileLeftHeader = (props: { onOpenDrawer: () => void, basePath: string }) => {
  const { onOpenDrawer, basePath } = props
  const history = useHistory()

  return (
    <HStack gap={2} alignItems="center">
      <IconButton onClick={onOpenDrawer} width="32px" height="32px">
        <Menu color={colors.grey900} fontSize={24} />
      </IconButton>
      <Link onClick={() => history.push(basePath)}>
        <HStack gap={2} alignItems="center">
          <Image src={stmLogo} alt="牧羊人經銷平台" width={80} height={32} objectFit="contain" />
          <Text variant="subtitle" color={colors.grey900}>牧羊人經銷平台</Text>
        </HStack>
      </Link>
    </HStack>
  )
}

const MobileRightHeader = (props: { onLogout?: () => void, basePath: string, cartItemQuantity: number }) => {
  const { onLogout, basePath, cartItemQuantity } = props
  const history = useHistory()

  return (
    <HStack alignItems="center">
      <Link onClick={onLogout}>
        <Text variant="caption" color={colors.blue600}>登出</Text>
      </Link>
      <IconButton onClick={() => history.push(`${basePath}/my-account/order`)} width="32px" height="32px">
        <UserOutlined color={colors.grey900} fontSize={20} />
      </IconButton>
      <AppBarCart
        onClick={() => history.push(`${basePath}/cart`)}
        cartItemQuantity={cartItemQuantity}
      />
    </HStack>
  )
}

export const Header = (props: HeaderProps) => {
  const { onLogout, isAuth = true } = props
  const isMobile = useResponsive('mobile')
  const history = useHistory()
  const { url: basePath } = useRouteMatch()
  const [activeCategory, setActiveCategory] = useState<BrandCategory | null>(null)
  const [drawerOpen, setDrawerOpen] = useState(false)

  const handleToggleCategory = useCallback((category: BrandCategory) => {
    setActiveCategory(prev => prev === category ? null : category)
  }, [])

  const handleClearCategory = useCallback(() => {
    setActiveCategory(null)
  }, [])

  const { setBrand } = useStmBrandStore()
  const activeBrands = useActiveBrands()
  const { destructuredData: userInfo } = useApiCampaignMember(activeBrands[0] ? { code: activeBrands[0].campaignCode } : undefined)
  const userName = userInfo?.user_name ?? userInfo?.account ?? ''
  const cartItemQuantity = useStmAllBrandsCartQuantity()

  const handleSelectBrand = useCallback((brand: BrandInfo) => {
    setActiveCategory(null)
    setBrand(brand.campaignCode)
    history.push(`${basePath}/product-catalog`)
  }, [setBrand, basePath, history])

  if (!isAuth) {
    return (
      <AppBar
        left={<LogoHeader basePath={basePath} />}
      />
    )
  }

  return (
    <>
      <AppBar
        left={
          isMobile
            ? <MobileLeftHeader onOpenDrawer={() => setDrawerOpen(true)} basePath={basePath} />
            : <DesktopLeftHeader activeCategory={activeCategory} onToggleCategory={handleToggleCategory} onClearCategory={handleClearCategory} basePath={basePath} />
        }
        right={
          isMobile
            ? <MobileRightHeader onLogout={onLogout} basePath={basePath} cartItemQuantity={cartItemQuantity} />
            : <DesktopRightHeader onLogout={onLogout} basePath={basePath} userName={userName} cartItemQuantity={cartItemQuantity} />
        }
      />
      {!isMobile && (
        <BrandNavDropdown activeCategory={activeCategory} onSelectBrand={handleSelectBrand} />
      )}
      {isMobile && (
        <BrandNavDrawer
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          onSelectBrand={handleSelectBrand}
        />
      )}
    </>
  )
}
